import { create } from 'zustand';
import { createClientRequestId } from '../services/apiService';
import { jobAPI } from '../services/api';
import useJobStore from './jobStore';

const asRunMap = (runs = []) => Object.fromEntries(
  runs.filter(Boolean).map((run) => [String(run.id), run])
);

const mergeRun = (current = {}, incoming = {}) => ({
  ...current,
  ...incoming,
  coreData: {
    ...(current.coreData || {}),
    ...(incoming.coreData || {})
  },
  sections: {
    ...(current.sections || {}),
    ...(incoming.sections || {})
  }
});

const getStoredLastEventId = () => {
  try {
    return Number(sessionStorage.getItem('jobProcessingLastEventId') || 0);
  } catch (_error) {
    return 0;
  }
};

const storeLastEventId = (value) => {
  try {
    sessionStorage.setItem('jobProcessingLastEventId', String(value));
  } catch (_error) {
    // Durable backend state remains the source of truth when browser storage is unavailable.
  }
};

const useJobProcessingStore = create((set, get) => ({
  runs: {},
  initialized: false,
  loading: false,
  streamConnected: false,
  lastEventId: getStoredLastEventId(),
  dismissedRunIds: {},
  uploadingImportCount: 0,
  error: null,

  hydrate: async () => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.getActionableImports();
      const incoming = asRunMap(response.processing || []);
      set(state => ({
        runs: Object.entries(incoming).reduce((result, [id, run]) => ({
          ...result,
          [id]: mergeRun(state.runs[id], run)
        }), {}),
        initialized: true,
        loading: false,
        error: null
      }));
      return { success: true, runs: response.processing || [] };
    } catch (error) {
      set({ initialized: true, loading: false, error: error?.message || error?.error || 'Could not load job processing status.' });
      return { success: false, error };
    }
  },

  startImport: async (file, options = {}) => {
    const idempotencyKey = options.idempotencyKey || createClientRequestId('job-import');
    set(state => ({ uploadingImportCount: state.uploadingImportCount + 1 }));
    try {
      const response = await jobAPI.createImport(file, {
        ...options,
        idempotencyKey
      });
      const run = response.processing;
      if (run?.id) {
        set(state => ({
          runs: {
            ...state.runs,
            [String(run.id)]: mergeRun(state.runs[String(run.id)], run)
          }
        }));
      }
      if (response.job) {
        useJobStore.getState().upsertJob(response.job);
      }
      return { success: true, ...response };
    } catch (error) {
      return {
        success: false,
        error: error?.message || error?.error || error?.response?.error || 'Could not create the draft.'
      };
    } finally {
      set(state => ({ uploadingImportCount: Math.max(0, state.uploadingImportCount - 1) }));
    }
  },

  registerRun: (run) => {
    if (!run?.id) return;
    set(state => ({
      runs: {
        ...state.runs,
        [String(run.id)]: mergeRun(state.runs[String(run.id)], run)
      }
    }));
  },

  applyEvent: (event) => {
    if (!event?.processing?.id) return;
    const runId = String(event.processing.id);
    set(state => {
      const current = state.runs[runId] || {};
      const sectionUpdate = event.type?.startsWith('section.') && event.section
        ? { [event.section]: event.payload?.fields || {} }
        : {};
      return {
        runs: {
          ...state.runs,
          [runId]: mergeRun(current, {
            ...event.processing,
            sections: sectionUpdate,
            latestEvent: event
          })
        },
        lastEventId: Math.max(Number(state.lastEventId || 0), Number(event.id || 0))
      };
    });

    const nextAfter = Math.max(Number(get().lastEventId || 0), Number(event.id || 0));
    storeLastEventId(nextAfter);
    if (event.type?.startsWith('section.') && event.payload?.fields) {
      useJobStore.getState().applyJobSection(event.processing.jobId, event.payload.fields);
    }
    useJobStore.getState().applyJobProcessing(event.processing.jobId, event.processing);
  },

  setStreamConnected: (connected) => set({ streamConnected: connected }),

  retry: async (runId) => {
    try {
      const response = await jobAPI.retryImport(runId);
      get().registerRun(response.processing);
      return { success: true, processing: response.processing };
    } catch (error) {
      return { success: false, error: error?.message || error?.error || 'Could not retry processing.' };
    }
  },

  cancel: async (runId) => {
    try {
      const response = await jobAPI.cancelImport(runId);
      get().registerRun(response.processing);
      return { success: true, processing: response.processing };
    } catch (error) {
      return { success: false, error: error?.message || error?.error || 'Could not cancel processing.' };
    }
  },

  dismiss: (runId) => set(state => ({
    dismissedRunIds: { ...state.dismissedRunIds, [String(runId)]: true }
  }))
}));

export default useJobProcessingStore;
